import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FileText, FolderOpen, Menu, TrendingUp, Eye, PenSquare } from 'lucide-react';
import axios from 'axios';
import AdminLayout from '@/components/AdminLayout';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [recentBlogs, setRecentBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const { getAuthHeaders } = useAuth();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, blogsRes] = await Promise.all([
          axios.get(`${API}/stats`, { headers: getAuthHeaders() }),
          axios.get(`${API}/blogs`, { headers: getAuthHeaders() })
        ]);
        setStats(statsRes.data);
        setRecentBlogs(blogsRes.data.slice(0, 5));
      } catch (error) {
        console.error('Failed to fetch dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [getAuthHeaders]);

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const statCards = [
    { 
      icon: FileText, 
      label: 'Total Posts', 
      value: stats?.total_blogs || 0, 
      color: 'from-[#6F42F0] to-[#9F7AEA]',
      link: '/admin/blogs'
    },
    { 
      icon: Eye, 
      label: 'Published', 
      value: stats?.published_blogs || 0, 
      color: 'from-[#10B981] to-[#34D399]',
      link: '/admin/blogs'
    },
    { 
      icon: PenSquare, 
      label: 'Drafts', 
      value: stats?.draft_blogs || 0, 
      color: 'from-[#F59E0B] to-[#FBBF24]',
      link: '/admin/blogs'
    },
    { 
      icon: FolderOpen, 
      label: 'Categories', 
      value: stats?.total_categories || 0, 
      color: 'from-[#EC4899] to-[#F472B6]',
      link: '/admin/categories'
    },
    { 
      icon: Menu, 
      label: 'Menu Links', 
      value: stats?.total_menus || 0, 
      color: 'from-[#3B82F6] to-[#60A5FA]',
      link: '/admin/menus'
    },
  ];

  return (
    <AdminLayout>
      <div className="page-transition" data-testid="admin-dashboard">
        {/* Header */}
        <div className="mb-8">
          <h1 className="font-['Manrope'] text-2xl md:text-3xl font-bold text-slate-900 mb-2">
            Dashboard
          </h1>
          <p className="text-slate-500">Welcome back! Here's an overview of your blog.</p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-8">
          {loading ? (
            Array(5).fill(0).map((_, i) => (
              <div key={i} className="bg-white rounded-2xl p-6 border border-slate-100">
                <div className="skeleton h-10 w-10 rounded-xl mb-4"></div>
                <div className="skeleton h-8 w-16 mb-2 rounded"></div>
                <div className="skeleton h-4 w-20 rounded"></div>
              </div>
            ))
          ) : (
            statCards.map((stat, index) => {
              const Icon = stat.icon;
              return (
                <Link 
                  key={index} 
                  to={stat.link}
                  className="stat-card bg-white rounded-2xl p-6 border border-slate-100 hover:border-slate-200"
                  data-testid={`stat-${stat.label.toLowerCase().replace(' ', '-')}`}
                >
                  <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${stat.color} flex items-center justify-center mb-4 shadow-lg`}>
                    <Icon className="w-5 h-5 text-white" />
                  </div>
                  <p className="font-['Manrope'] text-2xl font-bold text-slate-900 mb-1">
                    {stat.value}
                  </p>
                  <p className="text-slate-500 text-sm">{stat.label}</p>
                </Link>
              );
            })
          )}
        </div>

        {/* Recent Posts */}
        <Card className="border-slate-100">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="font-['Manrope'] text-lg font-semibold">
              Recent Posts
            </CardTitle>
            <Link 
              to="/admin/blogs" 
              className="text-[#6F42F0] text-sm font-medium hover:underline"
            >
              View All
            </Link>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="space-y-4">
                {Array(3).fill(0).map((_, i) => (
                  <div key={i} className="flex items-center gap-4 p-4 rounded-xl bg-slate-50">
                    <div className="skeleton h-12 w-12 rounded-lg"></div>
                    <div className="flex-1">
                      <div className="skeleton h-5 w-48 mb-2 rounded"></div>
                      <div className="skeleton h-4 w-24 rounded"></div>
                    </div>
                  </div>
                ))}
              </div>
            ) : recentBlogs.length === 0 ? (
              <div className="text-center py-12">
                <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500 mb-4">No blog posts yet</p>
                <Link 
                  to="/admin/blogs/new"
                  className="btn-primary px-6 py-2.5 rounded-full text-white font-medium text-sm inline-block"
                >
                  Create Your First Post
                </Link>
              </div>
            ) : (
              <div className="space-y-3">
                {recentBlogs.map((blog) => (
                  <Link
                    key={blog.id}
                    to={`/admin/blogs/${blog.id}`}
                    className="flex items-center gap-4 p-4 rounded-xl hover:bg-slate-50 transition-colors admin-table-row"
                    data-testid={`recent-blog-${blog.id}`}
                  >
                    {/* Thumbnail */}
                    <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-[#6F42F0]/10 to-[#10B981]/10 flex items-center justify-center flex-shrink-0 overflow-hidden">
                      {blog.featured_image ? (
                        <img 
                          src={blog.featured_image.startsWith('/api') 
                            ? `${process.env.REACT_APP_BACKEND_URL}${blog.featured_image}` 
                            : blog.featured_image}
                          alt=""
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <FileText className="w-5 h-5 text-[#6F42F0]" />
                      )}
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <h3 className="font-medium text-slate-900 truncate">{blog.title}</h3>
                      <p className="text-sm text-slate-500">
                        {formatDate(blog.created_at)}
                        {blog.category_name && ` • ${blog.category_name}`}
                      </p>
                    </div>

                    {/* Status Badge */}
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                      blog.status === 'published' 
                        ? 'status-published' 
                        : 'status-draft'
                    }`}>
                      {blog.status}
                    </span>
                  </Link>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Quick Actions */}
        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
          <Link 
            to="/admin/blogs/new"
            className="flex items-center gap-4 p-6 bg-gradient-to-br from-[#6F42F0] to-[#9F7AEA] rounded-2xl text-white hover:shadow-lg hover:shadow-purple-500/30 transition-shadow"
            data-testid="quick-action-new-post"
          >
            <FileText className="w-8 h-8" />
            <div>
              <h3 className="font-semibold text-lg">Create New Post</h3>
              <p className="text-white/80 text-sm">Write and publish a new article</p>
            </div>
          </Link>

          <Link 
            to="/admin/categories"
            className="flex items-center gap-4 p-6 bg-white border border-slate-200 rounded-2xl hover:border-slate-300 transition-colors"
            data-testid="quick-action-categories"
          >
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#EC4899]/10 to-[#F472B6]/10 flex items-center justify-center">
              <FolderOpen className="w-6 h-6 text-[#EC4899]" />
            </div>
            <div>
              <h3 className="font-semibold text-slate-900">Manage Categories</h3>
              <p className="text-slate-500 text-sm">Organize your content</p>
            </div>
          </Link>

          <Link 
            to="/admin/menus"
            className="flex items-center gap-4 p-6 bg-white border border-slate-200 rounded-2xl hover:border-slate-300 transition-colors"
            data-testid="quick-action-menus"
          >
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#3B82F6]/10 to-[#60A5FA]/10 flex items-center justify-center">
              <Menu className="w-6 h-6 text-[#3B82F6]" />
            </div>
            <div>
              <h3 className="font-semibold text-slate-900">Edit Menu Links</h3>
              <p className="text-slate-500 text-sm">Customize navigation</p>
            </div>
          </Link>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminDashboard;
